import { useEffect, useSyncExternalStore } from 'react';
import { supabaseClient } from '../config/supabase';

export const PINNED_TEMPLATES_PREFERENCE_KEY = 'communications.pinned_templates';
export const MAX_PINNED_TEMPLATES = 5;

export function normalizePinnedTemplateSids(value) {
    if (!Array.isArray(value)) return [];
    return [...new Set(value.filter((sid) => typeof sid === 'string' && sid.trim()).map((sid) => sid.trim()))]
        .slice(0, MAX_PINNED_TEMPLATES * 3);
}

export async function loadPinnedTemplateSids(client, userId) {
    if (!client || !userId) return [];
    const { data, error } = await client.from('user_preferences')
        .select('value')
        .eq('user_id', userId)
        .eq('key', PINNED_TEMPLATES_PREFERENCE_KEY)
        .maybeSingle();
    if (error) throw error;
    return normalizePinnedTemplateSids(data?.value);
}

export async function savePinnedTemplateSids(client, userId, sids, { channel, templates = [] } = {}) {
    if (!client || !userId) throw new Error('Sign in to save pinned templates.');
    const normalized = normalizePinnedTemplateSids(sids);
    const total = new Set((Array.isArray(sids) ? sids : []).filter(id => typeof id === 'string' && id.trim()).map(id => id.trim())).size;
    const channelIds = new Set(templates.filter(t => !t.channel || t.channel === channel).map(t => t.sid || t.id));
    const count = channel ? normalized.filter(id => channelIds.has(id)).length : normalized.length;
    if (count > MAX_PINNED_TEMPLATES || total > MAX_PINNED_TEMPLATES * 3) {
        throw new Error(`Choose no more than ${MAX_PINNED_TEMPLATES} pinned templates.`);
    }
    const { error } = await client.from('user_preferences').upsert({
        user_id: userId,
        key: PINNED_TEMPLATES_PREFERENCE_KEY,
        value: normalized,
        updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id,key' });
    if (error) throw error;
    return normalized;
}

const stores = new WeakMap();
function preferenceStore(client, userId) {
    if (!stores.has(client)) stores.set(client, new Map());
    const people = stores.get(client);
    const key = userId || '';
    if (!people.has(key)) {
        let state = { ids: [], loaded: !userId, saving: false, error: '' };
        let started = false;
        const listeners = new Set();
        const publish = (patch) => {
            state = { ...state, ...patch };
            listeners.forEach((listener) => listener());
        };
        people.set(key, {
            subscribe(listener) {
                listeners.add(listener);
                return () => listeners.delete(listener);
            },
            snapshot: () => state,
            load() {
                if (started || !userId) return;
                started = true;
                loadPinnedTemplateSids(client, userId)
                    .then((ids) => publish({ ids, loaded: true, error: '' }))
                    .catch((error) => {
                        started = false;
                        const message = error?.message || 'Could not load pinned templates.';
                        publish({ loaded: true, error: message });
                        console.error('Error loading pinned template preference:', error);
                    });
            },
            async save(ids, scope = {}) {
                if (!userId || state.saving) return;
                const previous = state.ids;
                const next = normalizePinnedTemplateSids(ids);
                publish({ ids: next, saving: true, error: '' });
                try {
                    await savePinnedTemplateSids(client, userId, ids, scope);
                    publish({ loaded: true });
                } catch (error) {
                    const message = error?.message || 'Could not save pinned templates.';
                    publish({ ids: previous, error: message });
                    throw error;
                } finally {
                    publish({ saving: false });
                }
            },
        });
    }
    return people.get(key);
}

export function usePinnedTemplatePreference(userId, client = supabaseClient) {
    const store = preferenceStore(client, userId);
    const state = useSyncExternalStore(store.subscribe, store.snapshot, store.snapshot);
    useEffect(() => { store.load(); }, [store]);
    return { ...state, save: store.save };
}
